/**
 * Excerpt Panel Component
 * Slide-in panel that shows extracted text excerpt when a citation is clicked
 * Slides from bottom on mobile, from right on desktop
 */

import { useEffect, useCallback } from 'react';
import { X, FileText, ExternalLink } from 'lucide-react';
import type { Citation } from '../../types/review';

interface ExcerptPanelProps {
  /** The citation to display, or null to hide the panel */
  citation: Citation | null;
  /** Callback when panel is closed */
  onClose: () => void;
  /** Callback to navigate to a specific page */
  onGoToPage: (page: number) => void;
}

// Evidence type display labels and colors
const EVIDENCE_TYPE_CONFIG: Record<string, { label: string; bgColor: string; textColor: string }> = {
  diagnosis: { label: 'Diagnosis', bgColor: 'bg-purple-100 dark:bg-purple-900/40', textColor: 'text-purple-700 dark:text-purple-300' },
  medication: { label: 'Medication', bgColor: 'bg-blue-100 dark:bg-blue-900/40', textColor: 'text-blue-700 dark:text-blue-300' },
  procedure: { label: 'Procedure', bgColor: 'bg-green-100 dark:bg-green-900/40', textColor: 'text-green-700 dark:text-green-300' },
  lab_result: { label: 'Lab Result', bgColor: 'bg-amber-100 dark:bg-amber-900/40', textColor: 'text-amber-700 dark:text-amber-300' },
  vital_sign: { label: 'Vital Sign', bgColor: 'bg-pink-100 dark:bg-pink-900/40', textColor: 'text-pink-700 dark:text-pink-300' },
  allergy: { label: 'Allergy', bgColor: 'bg-red-100 dark:bg-red-900/40', textColor: 'text-red-700 dark:text-red-300' },
  history: { label: 'History', bgColor: 'bg-indigo-100 dark:bg-indigo-900/40', textColor: 'text-indigo-700 dark:text-indigo-300' },
  note: { label: 'Clinical Note', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-700 dark:text-gray-300' },
  exclusion: { label: 'Exclusion', bgColor: 'bg-red-100 dark:bg-red-900/40', textColor: 'text-red-700 dark:text-red-300' },
  inclusion: { label: 'Inclusion', bgColor: 'bg-green-100 dark:bg-green-900/40', textColor: 'text-green-700 dark:text-green-300' },
  default: { label: 'Evidence', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-700 dark:text-gray-300' },
};

function getEvidenceTypeConfig(evidenceType: string) {
  return EVIDENCE_TYPE_CONFIG[evidenceType.toLowerCase()] || EVIDENCE_TYPE_CONFIG.default;
}

export function ExcerptPanel({ citation, onClose, onGoToPage }: ExcerptPanelProps) {
  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && citation) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [citation, onClose]);

  // Prevent body scroll when panel is open on mobile
  useEffect(() => {
    if (citation) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [citation]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleGoToPage = useCallback(() => {
    if (citation) {
      onGoToPage(citation.page_number);
    }
  }, [citation, onGoToPage]);

  if (!citation) {
    return null;
  }

  const evidenceConfig = getEvidenceTypeConfig(citation.evidence_type);

  return (
    <>
      {/* Backdrop - only visible on mobile */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel - bottom on mobile, right side on desktop */}
      <div
        className={`
          fixed z-50 bg-white dark:bg-gray-800 shadow-xl
          inset-x-0 bottom-0 rounded-t-xl max-h-[70vh]
          md:inset-y-0 md:right-0 md:left-auto md:top-0 md:bottom-0 md:rounded-none md:rounded-l-xl md:max-h-none md:w-96
          transform transition-transform duration-300 ease-out
          ${citation ? 'translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-y-0 md:translate-x-full'}
        `}
        role="dialog"
        aria-label="Citation excerpt"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-t-xl md:rounded-none md:rounded-tl-xl">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-gray-500 dark:text-gray-400" />
            <span className="font-medium text-gray-900 dark:text-gray-100">Source Excerpt</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            aria-label="Close panel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(70vh-120px)] md:max-h-[calc(100vh-120px)]">
          {/* Page number and evidence type badges */}
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/40 rounded-full">
              Page {citation.page_number}
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-1 text-sm font-medium rounded-full ${evidenceConfig.bgColor} ${evidenceConfig.textColor}`}
            >
              {evidenceConfig.label}
            </span>
          </div>

          {/* Extracted text excerpt */}
          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {citation.extracted_text}
            </p>
          </div>

          {/* Help text */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            This excerpt was extracted from the uploaded medical history document and matched to
            the criterion being evaluated.
          </p>
        </div>

        {/* Footer with action button */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 md:rounded-bl-xl">
          <button
            onClick={handleGoToPage}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            <ExternalLink size={16} />
            Go to page {citation.page_number}
          </button>
        </div>
      </div>
    </>
  );
}

export default ExcerptPanel;
